import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatError, photoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle, ArrowLeft, Smartphone } from "lucide-react";
import { toast } from "sonner";

// Helper function to check if user is online
const isUserOnline = (lastActiveAt) => {
  if (!lastActiveAt) return false;
  try {
    const diffMs = new Date() - new Date(lastActiveAt);
    return diffMs < 5 * 60 * 1000; // Active within last 5 minutes
  } catch (e) {
    return false;
  }
};

export default function Chat() {
  const { id: otherId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [convs, setConvs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [other, setOther] = useState(null);
  const [smsFallback, setSmsFallback] = useState(false);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadConvs = async () => {
    try { 
      const { data } = await api.get("/conversations"); 
      setConvs(data); 
    } catch (e) {
      console.error("Failed to load conversations", e);
    }
  };

  useEffect(() => { 
    loadConvs(); 
  }, [otherId]); // reload conversations list when changing active user to sync last message

  useEffect(() => {
    if (!otherId) { 
      setMsgs([]); 
      setOther(null); 
      return; 
    }
    
    (async () => {
      try {
        const [{ data: m }, { data: p }] = await Promise.all([
          api.get(`/messages/${otherId}`),
          api.get(`/profile/${otherId}`),
        ]);
        setMsgs(m); 
        setOther(p);
      } catch (e) { 
        toast.error(formatError(e)); 
      }
    })();

    const interval = setInterval(async () => {
      try { 
        const { data } = await api.get(`/messages/${otherId}`); 
        setMsgs(data); 
      } catch {}
    }, 4000);

    return () => clearInterval(interval);
  }, [otherId]);

  useEffect(() => { 
    endRef.current?.scrollIntoView({ behavior: "smooth" }); 
  }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !otherId) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { 
        to_user_id: otherId, 
        text: text.trim()
      });
      setMsgs((m) => [...m, data]);
      setText("");
      if (!isUserOnline(other?.last_active_at)) {
        toast.success("Message sent! An email notification has been dispatched to your offline match.");
      }
      loadConvs();
    } catch (e) { 
      toast.error(formatError(e)); 
    } finally {
      setSending(false);
    }
  };

  // Filter online matches for horizontal list
  const onlineMatches = convs.filter(c => isUserOnline(c.user.last_active_at));

  return (
    <div className="container-px py-8 max-w-6xl mx-auto text-left">
      <span className="label-cap">Direct Chat</span>
      <h1 className="font-heading text-4xl text-primary mt-1 mb-6">Matches Inbox</h1>

      <div className="grid lg:grid-cols-12 gap-6">
        
        {/* Sidebar: Conversation Thread list */}
        <aside 
          className={`${
            otherId ? "hidden lg:block lg:col-span-4" : "col-span-12 lg:col-span-4"
          } card-soft p-0 max-h-[75vh] overflow-y-auto flex flex-col`}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-border bg-slate-50/50 shrink-0 font-semibold text-xs text-muted-foreground uppercase tracking-wider">
            Conversations
          </div>

          {/* Horizontal Online Matches Section */}
          {onlineMatches.length > 0 && (
            <div className="px-4 py-3 border-b border-border bg-emerald-50/5 shrink-0">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                Online Matches ({onlineMatches.length})
              </div>
              <div className="flex gap-4 overflow-x-auto pb-1 scrollbar-thin">
                {onlineMatches.map((c) => (
                  <button 
                    key={c.user.user_id}
                    onClick={() => nav(`/app/chat/${c.user.user_id}`)}
                    className="flex flex-col items-center shrink-0 w-12 text-center group"
                  >
                    <div className="relative shrink-0">
                      <img 
                        src={photoUrl(c.user.photos?.[0]) || "https://images.unsplash.com/photo-1535930891776-0c2dfb7fda1a?w=200"}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover border border-slate-200 group-hover:scale-105 transition-all"
                      />
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border border-white rounded-full"></span>
                    </div>
                    <span className="text-[9px] text-slate-600 truncate w-full mt-1 font-semibold group-hover:text-primary transition-colors">
                      {c.user.name.split(" ")[0]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {convs.length === 0 ? (
              <div className="p-8 text-sm text-muted-foreground text-center italic border border-dashed rounded-xl m-2">
                No active conversations yet. Match mutually with other profiles to start a chat.
              </div>
            ) : convs.map((c) => {
              const online = isUserOnline(c.user.last_active_at);
              const selected = otherId === c.user.user_id;
              return (
                <button
                  key={c.user.user_id}
                  onClick={() => nav(`/app/chat/${c.user.user_id}`)}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    selected 
                      ? "bg-[#6B1A24] text-white shadow-sm" 
                      : "hover:bg-slate-50 border border-transparent hover:border-slate-100"
                  }`}
                  data-testid={`conv-${c.user.user_id}`}
                >
                  <div className="relative shrink-0">
                    <img 
                      src={photoUrl(c.user.photos?.[0]) || "https://images.unsplash.com/photo-1535930891776-0c2dfb7fda1a?w=200"} 
                      alt="" 
                      className={`w-11 h-11 rounded-full object-cover border ${selected ? "border-white/20" : "border-slate-200"}`}
                    />
                    {online && (
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border border-white rounded-full"></span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`font-semibold text-sm truncate ${selected ? "text-white" : "text-[#2A0A0E]"}`}>
                      {c.user.name}
                    </div>
                    <div className={`text-xs truncate mt-0.5 ${selected ? "text-white/80" : "text-slate-500"}`}>
                      {c.last_message?.text || "Click to say hello..."}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Chat Window */}
        <section 
          className={`${
            otherId ? "col-span-12 lg:col-span-8" : "hidden lg:flex lg:col-span-8"
          } card-soft flex flex-col h-[75vh]`}
        >
          {!otherId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
              <MessageCircle className="w-12 h-12 text-slate-300" strokeWidth={1} />
              <p className="font-semibold text-slate-500 text-sm">Select a conversation thread to start messaging.</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-border flex items-center justify-between gap-3 bg-slate-50/50 shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => nav("/app/chat")} 
                    className="lg:hidden rounded-full shrink-0 hover:bg-slate-100"
                    data-testid="chat-back-mobile"
                  >
                    <ArrowLeft className="w-5 h-5 text-slate-700" />
                  </Button>
                  
                  <Link to={`/app/profile/${otherId}`} className="shrink-0 relative">
                    <img 
                      src={photoUrl(other?.photos?.[0]) || "https://images.unsplash.com/photo-1535930891776-0c2dfb7fda1a?w=200"} 
                      alt="" 
                      className="w-10 h-10 rounded-full object-cover border border-slate-200" 
                    />
                    {isUserOnline(other?.last_active_at) && (
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border border-white rounded-full"></span>
                    )}
                  </Link>
                  <div className="min-w-0">
                    <Link to={`/app/profile/${otherId}`} className="font-heading text-base text-[#2A0A0E] hover:text-[#6B1A24] hover:underline truncate block font-bold leading-tight">
                      {other?.name || "Loading..."}
                    </Link>
                    <div className="text-[11px] text-slate-500 truncate flex items-center gap-1.5 mt-0.5">
                      {isUserOnline(other?.last_active_at) ? (
                        <span className="text-emerald-600 font-bold flex items-center gap-0.5">
                          Online
                        </span>
                      ) : (
                        <span>Offline</span>
                      )}
                      <span>·</span>
                      <span>{other?.district ? `${other.district}` : ""}{other?.occupation ? ` · ${other.occupation}` : ""}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat Message List */}
              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/20">
                {msgs.map((m) => {
                  const mine = m.from === user.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div 
                        className={`max-w-[70%] px-4 py-2 rounded-xl shadow-sm ${
                          mine 
                            ? "bg-[#6B1A24] text-white rounded-br-sm" 
                            : "bg-white text-slate-800 rounded-bl-sm border border-slate-200/60"
                        }`}
                      >
                        <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>
                        <div className={`text-[8px] mt-1 text-right ${mine ? "text-white/60" : "text-slate-400"}`}>
                          {new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              {/* Chat Input Form */}
              <form onSubmit={send} className="p-3 border-t border-border flex gap-2 bg-slate-50/40 shrink-0">
                <Input 
                  value={text} 
                  onChange={(e) => setText(e.target.value)} 
                  placeholder={smsFallback ? "Type SMS fallback message..." : "Type a message..."}
                  className="rounded-full bg-white border-slate-200 focus-visible:ring-[#6B1A24]/35" 
                  data-testid="chat-input" 
                  disabled={sending}
                />
                <Button 
                  type="submit" 
                  className="rounded-full bg-[#6B1A24] hover:bg-[#52141C] text-white shrink-0" 
                  data-testid="chat-send"
                  disabled={sending || !text.trim()}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </>
          )}
        </section>

      </div>
    </div>
  );
}
